import Base from 'ember-simple-auth/authenticators/base';
import config from 'admin-panel/config/environment';
import { decodeBase64Url } from 'admin-panel/utils/base64-url';

const { oidc, sentinel } = config;
const authorizationEndpoint = `${sentinel.host}/oauth/authorize`;
const tokenEndpoint = `${sentinel.host}/oauth/token`;

interface OidcSessionData {
  access_token: string;
  refresh_token: string;
  id_token: string;
  auth_token: string; // internal_token from id_token — backward compat with adapters
  client_id: string; // holds user_id: Sentinel's Token-auth header is `client_id=<user_uuid>`
  user_id: string;
  email: string;
  roles: string[];
  hotel_ids: string[];
  expires_at: number;
}

interface TokenResponse {
  access_token: string;
  refresh_token: string;
  id_token: string;
  token_type: string;
  expires_in: number;
}

interface IdTokenPayload {
  email: string;
  user_id: string;
  internal_token: string;
  roles: string[];
  hotel_ids?: string[];
  nonce?: string;
  [key: string]: unknown;
}

function decodeJwtPayload(jwt: string): IdTokenPayload {
  const parts = jwt.split('.');

  if (parts.length !== 3) {
    throw new Error('Invalid JWT format');
  }

  // Length check above guarantees parts[1] exists.
  return JSON.parse(decodeBase64Url(parts[1] as string));
}

/**
 * Validate that the id_token carries the app-specific claims we build the
 * session from. We trust the token's source via the TLS-backed code→token
 * exchange (Sentinel is internal, response comes from it directly), so we
 * deliberately don't validate iss/aud/exp/signature — Doorkeeper's contract
 * guarantees those for tokens minted for our client_id.
 *
 * What we DO want to catch loudly is contract drift on the Sentinel side:
 * a deploy that renames or omits one of these claims would otherwise build
 * a "successful" session with a broken auth header (silent 403s on every
 * API call) or empty role list (silent admin lockout). Throwing here turns
 * a multi-hour debug session into a one-line error.
 */
function validateIdTokenClaims(payload: IdTokenPayload): void {
  if (!payload.internal_token) {
    throw new Error('OIDC id_token missing required claim: internal_token');
  }

  if (!payload.user_id) {
    throw new Error('OIDC id_token missing required claim: user_id');
  }

  // Empty array is permitted (legitimately unprovisioned user). Missing
  // claim altogether is treated as a configuration error — without it,
  // hasRole(...) always returns false for the entire session.
  if (!Array.isArray(payload.roles)) {
    throw new Error('OIDC id_token missing required claim: roles');
  }

  // hotel_ids is omitted by Sentinel for users with no hotel scope
  // (admin, user roles), so missing is permitted; only validate shape
  // when present.
  if (payload.hotel_ids != null && !Array.isArray(payload.hotel_ids)) {
    throw new Error('OIDC id_token claim hotel_ids must be an array');
  }
}

function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);

  return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join(
    ''
  );
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);

  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

function buildSessionData(
  tokens: TokenResponse,
  payload: IdTokenPayload
): OidcSessionData {
  return {
    access_token: tokens.access_token,
    refresh_token: tokens.refresh_token,
    id_token: tokens.id_token,
    auth_token: payload.internal_token,
    client_id: payload.user_id, // Sentinel's Token-auth header encodes the user UUID as `client_id=`
    user_id: payload.user_id,
    email: payload.email,
    roles: payload.roles || [],
    hotel_ids: payload.hotel_ids || [],
    expires_at: Date.now() + tokens.expires_in * 1000,
  };
}

export default class OidcAuthenticator extends Base {
  async restore(data: OidcSessionData) {
    if (!data?.access_token) {
      throw new Error('No access token in session');
    }

    if (this.tokenExpired(data)) {
      return this.refreshOrFail(data);
    }

    return data;
  }

  private tokenExpired(data: OidcSessionData): boolean {
    return Boolean(data.expires_at && Date.now() >= data.expires_at);
  }

  private refreshOrFail(data: OidcSessionData): Promise<OidcSessionData> {
    if (!data.refresh_token) {
      throw new Error('Token expired and no refresh token available');
    }

    return this.refreshAccessToken(data.refresh_token);
  }

  async authenticate({
    code,
    codeVerifier,
    nonce,
  }: {
    code: string;
    codeVerifier: string;
    nonce: string;
  }) {
    const tokens = await this.exchangeCodeForTokens(code, codeVerifier);
    const payload = decodeJwtPayload(tokens.id_token);

    // Defense against id_token replay (an attacker substituting a stolen
    // id_token from another session). `state` covers the auth-step CSRF; the
    // nonce binds the id_token itself to this browser session.
    if (payload.nonce !== nonce) {
      throw new Error('OIDC nonce mismatch');
    }

    validateIdTokenClaims(payload);

    return buildSessionData(tokens, payload);
  }

  invalidate() {
    return Promise.resolve();
  }

  /**
   * Initiate the OIDC login flow by redirecting to Sentinel's authorization endpoint.
   * Call this from the login route — it stores PKCE params in localStorage (shared
   * across tabs so a callback landing in a different tab — e.g. after a
   * password-reset email opens in a new tab — can still validate) and performs a
   * full-page redirect.
   */
  static async redirectToLogin() {
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    const state = crypto.randomUUID();
    const nonce = crypto.randomUUID();

    localStorage.setItem('oidc_code_verifier', codeVerifier);
    localStorage.setItem('oidc_state', state);
    localStorage.setItem('oidc_nonce', nonce);

    const redirectUri = `${window.location.origin}${window.location.pathname}`;

    const params = new URLSearchParams({
      client_id: oidc.clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: oidc.scope,
      state,
      nonce,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    });

    OidcAuthenticator.navigateTo(
      `${authorizationEndpoint}?${params.toString()}`
    );
  }

  /** Extracted for testability — stubbed in tests to prevent navigation. */
  static navigateTo(url: string) {
    window.location.href = url;
  }

  /**
   * Read the OIDC callback params off the current URL. Extracted as a static
   * method so tests can stub it with a canned `{ code, state }` without
   * having to manipulate `window.location` / `window.history` directly.
   *
   * `error` is populated when the IdP rejects the auth request (e.g. user
   * cancelled, `error=access_denied&state=…` with no `code`). Without
   * surfacing it, the early-return in `handleOidcCallback` would let the
   * unauthenticated-bounce send the user back to /login → auto-SSO →
   * Sentinel re-issues the same error → infinite loop.
   */
  static readCallbackParams(): {
    code: string | null;
    state: string | null;
    error: string | null;
  } {
    const params = new URLSearchParams(window.location.search);
    return {
      code: params.get('code'),
      state: params.get('state'),
      error: params.get('error'),
    };
  }

  /**
   * Strip `?code=…&state=…` from the current URL after the callback has been
   * consumed (success or failure). Extracted alongside `readCallbackParams`
   * so tests can stub it into a no-op — keeps the hash-routing history
   * state out of the acceptance suite's setup.
   */
  static clearCallbackParamsFromUrl() {
    window.history.replaceState(
      {},
      '',
      window.location.pathname + window.location.hash
    );
  }

  private async exchangeCodeForTokens(
    code: string,
    codeVerifier: string
  ): Promise<TokenResponse> {
    const redirectUri = `${window.location.origin}${window.location.pathname}`;

    const response = await fetch(tokenEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        code,
        redirect_uri: redirectUri,
        client_id: oidc.clientId,
        code_verifier: codeVerifier,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));

      throw new Error(
        `Token exchange failed: ${
          (error as { error?: string }).error || response.statusText
        }`
      );
    }

    return response.json();
  }

  private async refreshAccessToken(
    refreshToken: string
  ): Promise<OidcSessionData> {
    const response = await fetch(tokenEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
        client_id: oidc.clientId,
      }),
    });

    if (!response.ok) {
      throw new Error('Token refresh failed');
    }

    const tokens: TokenResponse = await response.json();

    if (!tokens.id_token) {
      // Doorkeeper-openid_connect returns a fresh id_token on refresh. If
      // it's missing, treat as a contract break (loud error) rather than
      // silently feeding `undefined` into decodeJwtPayload — the resulting
      // TypeError is much harder to diagnose in production.
      throw new Error('OIDC token refresh response missing id_token');
    }

    const payload = decodeJwtPayload(tokens.id_token);
    validateIdTokenClaims(payload);

    return buildSessionData(tokens, payload);
  }
}
